import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import { assertNotNil } from '../../utils/types';

interface IBuilderContext {
  setJustExecuted: () => void;
  consumeJustExecuted: () => boolean;
}

interface IBuilderContextProviderProps {
  children: ReactNode;
}

const BuilderContext = createContext<IBuilderContext | null>(null);

export const BuilderContextProvider = ({
  children,
}: IBuilderContextProviderProps) => {
  const justExecuted = useRef<boolean>(false);

  const setJustExecuted = useCallback(() => {
    justExecuted.current = true;
  }, []);

  const consumeJustExecuted = useCallback(() => {
    const value = justExecuted.current;

    justExecuted.current = false;

    return value;
  }, []);

  const value = useMemo(
    () => ({
      setJustExecuted,
      consumeJustExecuted,
    }),
    [consumeJustExecuted, setJustExecuted],
  );

  return (
    <BuilderContext.Provider value={value}>{children}</BuilderContext.Provider>
  );
};

export const useBuilderContext = () => {
  const builderContext = useContext(BuilderContext);
  assertNotNil(builderContext);
  return builderContext;
};
